import React from 'react';
import { useTranslation } from 'react-i18next';
import type { CMField, CMStatusColour, CMSubSection, CMTable } from './add-edit.types';
import type { SectionFieldToolbarProps } from './utils/system-canvas-cm.types';
import { MAX_FIELDS_LIMIT, MAX_WIDTH_PCT } from './utils/constants';
import type { BombiixIconProps } from '../../../../bombiix-icon/types';
import {
  CalendarIcon,
  CheckBoxIcon,
  ChevronIcon,
  CommentsIcon,
  DesignDetailsIcon,
  DurationCounterIcon,
  FormulaIcon,
  ImageIcon,
  RadioButtonIcon,
  SourceIcon,
  StatusActiveIcon,
  TableViewIcon,
  TextboxIcon,
} from '../../../../bombiix-icon';
import { distributeEqualWidths, roundWidthPct } from './utils/system-canvas-cm.utils';

const SectionFieldToolbar: React.FC<SectionFieldToolbarProps> = ({
  sections,
  activeSectionIndex,
  setSections,
  forceDisableAll = false,
  targetSubIndex = null,
  showCommentBtn = true,
}) => {
  const { t } = useTranslation('custom-module-system-canvas');

  const getToolConfig = (kind: 'table' | CMField['type'] | 'comment'): { label: string; Icon?: React.FC<BombiixIconProps> } => {
    switch (kind) {
      case 'dorpdown':
        return { label: t('COMMON.FIELD_TYPES.DROPDOWN'), Icon: ChevronIcon };
      case 'text':
        return { label: t('COMMON.FIELD_TYPES.TEXT_BOX'), Icon: TextboxIcon };
      case 'radio':
        return { label: t('COMMON.FIELD_TYPES.RADIO_BUTTON'), Icon: RadioButtonIcon };
      case 'checkbox':
        return { label: t('COMMON.FIELD_TYPES.CHECKBOX'), Icon: CheckBoxIcon };
      case 'status':
        return { label: t('COMMON.FIELD_TYPES.STATUS'), Icon: StatusActiveIcon };
      case 'source':
        return { label: t('COMMON.FIELD_TYPES.SOURCE'), Icon: SourceIcon };
      case 'table':
        return { label: t('COMMON.FIELD_TYPES.TABLE'), Icon: TableViewIcon };
      case 'date-picker':
        return { label: t('COMMON.FIELD_TYPES.DATE_PICKER'), Icon: CalendarIcon };
      case 'comment':
        return { label: t('COMMON.FIELD_TYPES.COMMENT'), Icon: CommentsIcon };
      case 'image':
        return { label: t('COMMON.FIELD_TYPES.IMAGE'), Icon: ImageIcon };
      case 'calculation':
        return { label: t('COMMON.FIELD_TYPES.CALCULATION'), Icon: FormulaIcon };
      case 'canvas':
        return { label: t('COMMON.FIELD_TYPES.CANVAS'), Icon: DesignDetailsIcon };
      case 'time-picker':
        return { label: t('COMMON.FIELD_TYPES.TIME_PICKER'), Icon: DurationCounterIcon };
      default:
        return { label: String(kind) };
    }
  };

  /*
   * Disable logic is scoped to the target subsection (if provided).
   */
  const isToolDisabled = (kind: 'table' | CMField['type'] | 'comment') => {
    if (forceDisableAll) return true;
    if (activeSectionIndex === null) return true;
    const c: CMSubSection[] = sections[activeSectionIndex]?.content ?? [];
    const isSub = (x: unknown): x is CMSubSection => {
      const obj = x as Record<string, unknown>;
      return !!obj && typeof obj === 'object' && 'content' in obj && !('columns' in obj);
    };
    let subIdx: number = -1;
    if (typeof targetSubIndex === 'number' && targetSubIndex >= 0 && targetSubIndex < c.length && isSub(c[targetSubIndex])) {
      subIdx = targetSubIndex;
    } else {
      subIdx = [...c].map((contentItem) => isSub(contentItem)).lastIndexOf(true);
    }
    if (subIdx === -1) return true;
    const sub: CMSubSection = c[subIdx] as CMSubSection;
    const inner: Array<CMField | CMTable> = sub?.content ?? [];
    const isTable = (it: CMField | CMTable): it is CMTable => typeof (it as CMTable).columns !== 'undefined';
    const isCommentField = (it: CMField | CMTable): boolean => !isTable(it) && it.type === 'comment';
    const isRegularField = (it: CMField | CMTable): it is CMField => !isTable(it) && it.type !== 'comment';
    const subHasTable = inner.some((it) => isTable(it));
    const subHasComment = inner.some((it) => isCommentField(it));
    const subHasCanvas = inner.some((it) => !isTable(it) && STANDALONE_FIELD_TYPES.includes((it as CMField).type));
    const subFieldsCount = inner.filter((it) => isRegularField(it)).length;

    if (kind !== 'table' && kind !== 'comment' && !STANDALONE_FIELD_TYPES.includes(kind)) {
      return subHasTable || subHasComment || subHasCanvas || subFieldsCount >= MAX_FIELDS_LIMIT;
    }
    const subIsEmpty = inner.length === 0;
    if (kind === 'table') return !subIsEmpty;
    if (kind === 'comment') return !subIsEmpty;
    if (STANDALONE_FIELD_TYPES.includes(kind)) return !subIsEmpty;
    return true;
  };

  const addFieldToActive = (type: CMField['type']) => {
    if (activeSectionIndex === null) return;
    const section = sections[activeSectionIndex];
    const content: CMSubSection[] = section?.content ?? [];
    const isSub = (contentItem: unknown): contentItem is CMSubSection => {
      const obj = contentItem as Record<string, unknown>;
      return !!obj && typeof obj === 'object' && 'content' in obj && !('columns' in obj);
    };
    let targetIdx = [...content].map(isSub).lastIndexOf(true);
    if (typeof targetSubIndex === 'number' && targetSubIndex >= 0 && targetSubIndex < content.length && isSub(content[targetSubIndex])) {
      targetIdx = targetSubIndex;
    }
    if (targetIdx === -1) {
      const newSub: CMSubSection = { type: 'subSection', label: 'Sub Section 1', title: null, content: [] };
      const makeField = (): CMField => ({
        type,
        isRequired: false,
        title: ``,
        ...(type === 'dorpdown' || type === 'radio' || type === 'checkbox' ? { options: ['Option 1'] } : {}),
        ...(type === 'status'
          ? {
              statusColours: defaultStatusColours(),
            }
          : {}),
        value: null,
        ...(type === 'dorpdown' ? { isMultiple: false } : {}),
        ...(type === 'source' ? { isVisibleToSupplier: true } : {}),
        ...(type === 'calculation' ? { isVisibleToSupplier: true, calculationName: '', calculationPaths: [] } : {}),
        ...(type === 'canvas' ? { isVisibleToSupplier: true } : {}),
      });
      const newField = { ...makeField(), width: '100%' };
      newSub.content = [newField];
      setSections((prev) => prev.map((sec, si) => (si !== activeSectionIndex ? sec : { ...sec, content: [...content, newSub] })));
      return;
    }
    const target = content[targetIdx] as CMSubSection;
    const inner: Array<CMField | CMTable> = target?.content ?? [];
    const isTable = (item: CMField | CMTable): item is CMTable => typeof (item as CMTable).columns !== 'undefined';
    const isCommentField = (item: CMField | CMTable): boolean => !isTable(item) && item.type === 'comment';
    const isRegularField = (item: CMField | CMTable): item is CMField => !isTable(item) && item.type !== 'comment';
    const subHasTable = inner.some((item) => isTable(item));
    const subHasComment = inner.some((item) => isCommentField(item));
    if (subHasTable || subHasComment) return;
    if (STANDALONE_FIELD_TYPES.includes(type) && inner.length > 0) return;
    if (inner.some((item) => !isTable(item) && STANDALONE_FIELD_TYPES.includes(item.type))) return;
    const regularFields = inner.filter(isRegularField) as CMField[];
    if (type !== 'comment' && !STANDALONE_FIELD_TYPES.includes(type) && regularFields.length >= MAX_FIELDS_LIMIT) return;

    const totalPct = regularFields.reduce((sum, f) => {
      const fieldWidth = (f.width ?? '').trim();
      const match = /^(\d+(?:\.\d+)?)\s*%?$/.exec(fieldWidth);
      return sum + (match ? Number(match[1]) : 0);
    }, 0);
    const makeField = (): CMField => ({
      type,
      isRequired: false,
      title: ``,
      ...(type === 'dorpdown' || type === 'radio' || type === 'checkbox' ? { options: ['Option 1'] } : {}),
      ...(type === 'status'
        ? {
            statusColours: defaultStatusColours(),
          }
        : {}),
      value: null,
      ...(type === 'dorpdown' ? { isMultiple: false } : {}),
      ...(type === 'source' ? { isVisibleToSupplier: true } : {}),
      ...(type === 'calculation' ? { isVisibleToSupplier: true, calculationName: '', calculationPaths: [] } : {}),
      ...(type === 'canvas' ? { isVisibleToSupplier: true } : {}),
    });

    if (roundWidthPct(totalPct) >= MAX_WIDTH_PCT) {
      const equalWidths = distributeEqualWidths(regularFields.length + 1);
      let regularFieldIndex = 0;
      const updatedInner = inner.map((item) => {
        if (!isRegularField(item)) return item;
        const width = equalWidths[regularFieldIndex] ?? '';
        regularFieldIndex += 1;
        return { ...item, width };
      });
      const newField = { ...makeField(), width: equalWidths[equalWidths.length - 1] ?? '' };
      const updatedTarget: CMSubSection = { ...target, content: [...updatedInner, newField] };
      setSections((prev) =>
        prev.map((sec, si) => (si !== activeSectionIndex ? sec : { ...sec, content: content.map((c, ci) => (ci === targetIdx ? updatedTarget : c)) })),
      );
      return;
    }

    const remainingPct = roundWidthPct(MAX_WIDTH_PCT - totalPct);
    const newField = { ...makeField(), width: `${remainingPct}%` };
    const updatedTarget: CMSubSection = { ...target, content: [...inner, newField] };
    setSections((prev) =>
      prev.map((sec, si) => (si !== activeSectionIndex ? sec : { ...sec, content: content.map((c, ci) => (ci === targetIdx ? updatedTarget : c)) })),
    );
  };

  const addTableToActive = () => {
    if (activeSectionIndex === null) return;
    setSections((prev) =>
      prev.map((section, sectionIndex) => {
        if (sectionIndex !== activeSectionIndex) return section;
        const content: CMSubSection[] = section.content ?? [];
        const isSub = (contentItem: unknown): contentItem is CMSubSection => {
          const obj = contentItem as Record<string, unknown>;
          return !!obj && typeof obj === 'object' && 'content' in obj && !('columns' in obj);
        };

        const makeTable = (): CMTable => ({
          type: 'table',
          title: '',
          columns: [
            {
              type: 'text',
              isRequired: false,
              title: 'Column 1',
              value: null,
            } as CMField,
          ],
        });

        const lastSubIdx = [...content].map(isSub).lastIndexOf(true);
        let targetIdx = lastSubIdx;

        if (typeof targetSubIndex === 'number' && targetSubIndex >= 0 && targetSubIndex < content.length) {
          if (isSub(content[targetSubIndex])) {
            targetIdx = targetSubIndex;
          }
        }

        if (targetIdx === -1) {
          const newSub: CMSubSection = { type: 'subSection', label: 'Sub Section 1', title: null, content: [] };
          const newTable = makeTable();
          newSub.content = [...newSub.content, newTable];
          return { ...section, content: [...content, newSub] };
        }
        const target = content[targetIdx] as CMSubSection;
        const inner: Array<CMField | CMTable> = target?.content ?? [];
        const subIsEmpty = inner.length === 0;
        if (!subIsEmpty) return section;
        const newTable = makeTable();
        const updatedTarget: CMSubSection = { ...target, content: [...(target.content ?? []), newTable] };
        const nextContent = content.map((contentItem, contentIndex) => (contentIndex === targetIdx ? updatedTarget : contentItem));
        return { ...section, content: nextContent };
      }),
    );
  };

  const renderToolButton = (kind: 'table' | CMField['type'] | 'comment', onClick: () => void) => {
    const toolConfig = getToolConfig(kind);
    const disabled = isToolDisabled(kind);
    return (
      <button type="button" className="cm-tool" onClick={onClick} disabled={disabled} aria-disabled={disabled}>
        <span className="cm-tool-label">{toolConfig.label}</span>
        {toolConfig.Icon ? (
          <span className="cm-ico">
            <toolConfig.Icon size={TOOL_ICON_SIZE} />
          </span>
        ) : null}
      </button>
    );
  };

  /**
   * Statuses a new Status field starts with (Figma "Status CM"): Pending is the default.
   * @returns The starting status options.
   */
  const defaultStatusColours = (): CMStatusColour[] => [
    { statusName: t('COMMON.STATUS_COLORS.PENDING'), statusHexCode: '#FDD499', setDefault: true },
    { statusName: t('COMMON.STATUS_COLORS.APPROVED'), statusHexCode: '#73C5AF' },
    { statusName: t('COMMON.STATUS_COLORS.REJECTED'), statusHexCode: '#FA979B' },
    { statusName: t('COMMON.STATUS_COLORS.APPROVED_WITH_COMMENTS'), statusHexCode: '#DCF0EB' },
  ];

  return (
    <div className="cm-toolbar">
      {renderToolButton('dorpdown', () => addFieldToActive('dorpdown'))}
      {renderToolButton('text', () => addFieldToActive('text'))}
      {renderToolButton('radio', () => addFieldToActive('radio'))}
      {renderToolButton('checkbox', () => addFieldToActive('checkbox'))}
      {renderToolButton('table', () => addTableToActive())}
      {renderToolButton('image', () => addFieldToActive('image'))}
      {renderToolButton('date-picker', () => addFieldToActive('date-picker'))}
      {renderToolButton('time-picker', () => addFieldToActive('time-picker'))}
      {renderToolButton('source', () => addFieldToActive('source'))}
      {renderToolButton('canvas', () => addFieldToActive('canvas'))}
      {renderToolButton('calculation', () => addFieldToActive('calculation'))}
      {renderToolButton('status', () => addFieldToActive('status'))}
      {showCommentBtn && renderToolButton('comment', () => addFieldToActive('comment'))}
    </div>
  );
};

export default SectionFieldToolbar;

const TOOL_ICON_SIZE = 16;

const STANDALONE_FIELD_TYPES: ReadonlyArray<string> = ['canvas'];
